import React, { useId } from 'react';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import {
	FloatingGroup,
	IllustrationSvg,
	LuminanceMask,
	TwinkleGroup,
} from './RovoSearchHeaderIllustration.styles.compiled';

type PaletteKey =
	| 'brandBlue'
	| 'neutralSurface'
	| 'quadrantOrange'
	| 'quadrantPurple'
	| 'ringArc'
	| 'spark';

type Palette = Record<PaletteKey, string>;

const lightPalette: Palette = {
	brandBlue: '#1868DB',
	neutralSurface: '#DDDEE1',
	quadrantOrange: '#FCA700',
	quadrantPurple: '#AF59E1',
	ringArc: '#101214',
	spark: '#101214',
};

const darkPalette: Palette = {
	brandBlue: '#1558BC',
	neutralSurface: '#4B4D51',
	quadrantOrange: '#E56E00',
	quadrantPurple: '#AF59E1',
	ringArc: '#101214',
	spark: 'white',
};

const VIEWBOX_WIDTH = 90;
const VIEWBOX_HEIGHT = 92;

const LENS_MASK_PATH =
	'M45.371 45.371C35.0092 55.7327 18.1444 55.7327 7.77131 45.371C-2.59044 35.0092 -2.59044 18.1444 7.77131 7.77131C18.1331 -2.59044 34.9979 -2.59044 45.371 7.77131C55.7441 18.1331 55.7327 34.9979 45.371 45.371ZM14.5354 14.5354C7.9075 21.1633 7.9075 31.9677 14.5354 38.5955C21.1633 45.2234 31.9677 45.2234 38.5955 38.5955C45.2234 31.9677 45.2234 21.1633 38.5955 14.5354C31.9677 7.9075 21.1633 7.9075 14.5354 14.5354Z';

const LENS_GROUP_TRANSFORM = 'translate(22.7692 11.4289)';
const QUADRANT_GROUP_TRANSFORM = 'translate(-58.9063 -58.8858)';

const LENS_QUADRANTS: { transform: string; d: string; fill: PaletteKey }[] = [
	{
		transform: 'translate(17.7319 67.7445) rotate(-75.34)',
		d: 'M70.0241 0H0V70.0241H70.0241V0Z',
		fill: 'quadrantOrange',
	},
	{
		transform: 'translate(85.4754 85.4564) rotate(-75.34)',
		d: 'M70.0241 0H0V70.0241H70.0241V0Z',
		fill: 'quadrantPurple',
	},
	{
		transform: 'translate(67.7518 153.209) rotate(-75.34)',
		d: 'M70.0241 0H0V70.0241H70.0241V0Z',
		fill: 'brandBlue',
	},
	{
		transform: 'translate(2.91551e-05 135.475) rotate(-75.34)',
		d: 'M70.0241 0H0V70.0241H70.0241V0Z',
		fill: 'brandBlue',
	},
];

const BACKGROUND_SHEET = {
	transform: 'translate(-9.65338e-06 20.9926)',
	d: 'M45.5327 56.7683V13.5168C34.036 7.53582 18.4877 2.38332 0 0V43.0813C14.5496 45.5213 30.5859 50.061 45.5327 56.7683Z',
};

const HANDLE = {
	transform: 'translate(54.3395 49.4906)',
	d: 'M11.3264 0L0 8.17137L19.0325 34.5694L30.3589 26.3981L11.3264 0Z',
};

const SPARKS: { transform: string; d: string }[] = [
	{
		transform: 'translate(61.7325 2.16675e-05)',
		d: 'M0.0292604 10.5857C0.0746569 10.9489 1.31171 11.2213 1.516 10.9716C1.81107 10.5971 2.02671 10.1999 2.23099 9.66647C2.38988 9.03091 2.23099 8.78123 2.42393 8.36132C2.70766 7.63497 3.62694 6.6476 3.86527 6.43196C3.89931 6.39792 3.92201 6.36387 3.94471 6.31847C4.04685 6.01205 4.62566 4.6615 5.45415 3.74222C5.97621 3.04992 6.33938 2.6527 6.52096 2.44842C6.95223 1.96041 7.21326 1.54049 7.41754 1.04113C7.53104 0.76875 6.83874 -0.093784 6.52096 0.00835816C5.95351 0.178595 5.45415 0.473672 4.94343 1.01843C4.7505 1.22271 4.11495 1.4724 3.54749 2.1193C3.4567 2.22144 3.3886 2.34628 3.34321 2.48247C2.96869 3.66278 2.1175 4.89983 2.1175 4.89983C2.1175 4.89983 1.82242 5.37649 1.516 5.98935C1.19822 6.59085 0.8691 7.3172 0.676165 7.75981C0.403786 8.42941 0.290293 8.74719 0.222198 8.95147C0.00656439 9.56432 -0.0388344 10.0864 0.0292604 10.5857Z',
	},
	{
		transform: 'translate(72.0437 3.56768)',
		d: 'M0.068781 7.91519C-0.158202 8.15352 0.216315 9.36788 0.613534 9.34518C1.16964 9.33383 1.74845 9.17494 2.3613 8.81177C3.0309 8.34646 3.0082 7.99463 3.45082 7.72226C4.16581 7.2002 5.47097 6.4625 5.78874 6.28092C5.83414 6.25822 5.87953 6.22417 5.90223 6.17877C6.15191 5.89505 7.13928 4.60125 8.16071 3.72736C8.8303 3.05776 9.25023 2.68324 9.47721 2.49031C9.99927 2.01364 10.3057 1.60508 10.5781 1.11706C10.7256 0.844684 10.1128 -0.0745954 9.78363 0.00484854C9.18213 0.141038 8.63737 0.402068 8.05857 0.90143C7.83159 1.09436 7.17333 1.2873 6.51509 1.85476C6.41294 1.94555 6.32215 2.05904 6.25405 2.19523C5.69794 3.33014 4.56303 4.30617 4.56303 4.30617C4.56303 4.30617 4.16582 4.70339 3.6778 5.146C3.18979 5.59997 2.59963 6.07663 2.23646 6.34901C1.6917 6.74623 1.41932 6.91647 1.26043 7.00726C0.761072 7.31369 0.409252 7.59741 0.0574294 7.92654L0.068781 7.91519Z',
	},
	{
		transform: 'translate(76.2004 15.9021)',
		d: 'M0.0769798 3.78582C-0.172701 4.0128 0.23587 5.21581 0.576344 5.23851C1.05301 5.27256 1.52967 5.21581 2.09712 5.03423C2.72133 4.76185 2.78942 4.45542 3.24339 4.33058C3.98108 4.0809 5.33162 3.99011 5.66075 3.96741C5.70614 3.96741 5.75154 3.95606 5.79694 3.93336C6.10337 3.77447 7.41987 3.12757 8.64557 2.79845C9.48541 2.50337 10.0075 2.32179 10.2798 2.23099C10.9154 2.00401 11.358 1.70893 11.7552 1.32306C11.9709 1.10743 11.619 0.0633082 11.3126 0.0292608C10.7565 -0.038834 10.2344 0.00656263 9.58755 0.222196C9.33786 0.30164 8.71367 0.199498 7.96463 0.403782C7.83979 0.437829 7.72629 0.494576 7.62415 0.585369C6.72757 1.32306 5.36568 1.67489 5.36568 1.67489C5.36568 1.67489 3.37957 2.35583 2.53974 2.63956C1.90419 2.85519 1.60911 2.96869 1.41618 3.02543C0.848721 3.22972 0.440154 3.46805 0.0883314 3.79717L0.0769798 3.78582Z',
	},
];

const LENS_SHEET_CORNER = {
	transform: 'translate(32.3422 29.5842)',
	d: 'M4.9625 20.4511C7.31177 22.8004 10.1717 24.2985 13.1906 24.9794V4.92552C9.86532 3.20045 6.19955 1.54348 2.22736 0C-1.47246 6.4917 -0.564528 14.9241 4.9625 20.4511Z',
};

const RING_ARC = {
	transform: 'translate(22.788 26.4178)',
	d: 'M14.5163 23.6175C8.98928 18.0905 8.08135 9.65811 11.7812 3.16641C8.89849 2.04284 5.85692 0.987374 2.65647 0C-2.13286 9.90779 -0.453193 22.1762 7.75223 30.3816C11.9628 34.5921 17.2628 37.0663 22.7558 37.8493V28.1458C19.7369 27.4535 16.8656 25.9555 14.5277 23.6175H14.5163Z',
};

export interface RovoSearchHeaderIllustrationProps {
	/** Rendered width in pixels. Height follows the artwork's aspect ratio. */
	size?: number;
	testId?: string;
}

export const RovoSearchHeaderIllustration = ({
	size = 82,
	testId,
}: RovoSearchHeaderIllustrationProps): React.JSX.Element => {
	const { colorMode } = useThemeObserver();
	const palette = colorMode === 'dark' ? darkPalette : lightPalette;
	const maskId = `rovo-search-header-lens-${useId().replace(/:/g, '')}`;

	return (
		<IllustrationSvg
			width={size}
			height={Math.round((size * VIEWBOX_HEIGHT) / VIEWBOX_WIDTH)}
			viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden
			focusable={false}
			data-testid={testId}
		>
			<FloatingGroup>
				<g transform={LENS_GROUP_TRANSFORM}>
					<LuminanceMask
						id={maskId}
						maskUnits="userSpaceOnUse"
						x="0"
						y="0"
						width={VIEWBOX_WIDTH}
						height={VIEWBOX_HEIGHT}
					>
						{/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- White is luminance-mask math (fully visible), not a themeable colour. */}
						<path d={LENS_MASK_PATH} fill="white" />
					</LuminanceMask>
					<g mask={`url(#${maskId})`}>
						<g transform={QUADRANT_GROUP_TRANSFORM}>
							{LENS_QUADRANTS.map(({ transform, d, fill }) => (
								// The quadrants are one square placed four ways, so `d` is identical
								// across them and only `transform` is unique.
								<path key={transform} transform={transform} d={d} fill={palette[fill]} />
							))}
						</g>
					</g>
				</g>
				<path
					transform={BACKGROUND_SHEET.transform}
					d={BACKGROUND_SHEET.d}
					fill={palette.neutralSurface}
				/>
				<path transform={HANDLE.transform} d={HANDLE.d} fill={palette.brandBlue} />
				{SPARKS.map(({ transform, d }, index) => (
					<TwinkleGroup key={d} variant={index}>
						<path transform={transform} d={d} fill={palette.spark} />
					</TwinkleGroup>
				))}
				<path
					transform={LENS_SHEET_CORNER.transform}
					d={LENS_SHEET_CORNER.d}
					fill={palette.neutralSurface}
				/>
				<path transform={RING_ARC.transform} d={RING_ARC.d} fill={palette.ringArc} />
			</FloatingGroup>
		</IllustrationSvg>
	);
};
